/**
 * Loop brace model — contiguous pattern range over `clip.patterns` order.
 *
 * Product name: **loop brace** (the amber gutter strip in arrange).
 * Code / persistence: `patternLoop` / `PatternLoopRange` (keep stable in JSON).
 */
import { patternSteps, type ClipIR, type Pattern } from "../../../../src/lib/audio";

export type PatternLoopRange = {
  /** Inclusive start index into clip.patterns. */
  start: number;
  /** Number of patterns (≥ 1). */
  length: number;
};

export type ViewStepRef = {
  patternId: string;
  localStep: number;
  /** Index within the view list (0..length-1). */
  viewIndex: number;
};

export function defaultPatternLoop(patternCount = 1, activeIndex = 0): PatternLoopRange {
  const n = Math.max(1, patternCount | 0);
  const start = Math.max(0, Math.min(n - 1, activeIndex | 0));
  return { start, length: 1 };
}

/** Clamp a loop range into [0, patternCount). */
export function clampPatternLoop(
  range: PatternLoopRange | null | undefined,
  patternCount: number,
): PatternLoopRange {
  const n = Math.max(1, patternCount | 0);
  const rawStart = Math.round(Number(range?.start ?? 0));
  const rawLength = Math.round(Number(range?.length ?? 1));
  const length = Math.max(1, Math.min(n, Number.isFinite(rawLength) ? rawLength : 1));
  const start = Math.max(0, Math.min(n - length, Number.isFinite(rawStart) ? rawStart : 0));
  return { start, length };
}

export function sanitizePatternLoop(
  raw: unknown,
  patternCount: number,
): PatternLoopRange {
  if (!raw || typeof raw !== "object") {
    return defaultPatternLoop(patternCount);
  }
  const obj = raw as Record<string, unknown>;
  return clampPatternLoop(
    {
      start: typeof obj.start === "number" ? obj.start : 0,
      length: typeof obj.length === "number" ? obj.length : 1,
    },
    patternCount,
  );
}

/** Snap the loop brace onto a single pattern index. */
export function patternLoopAtIndex(index: number, patternCount: number): PatternLoopRange {
  const n = Math.max(1, patternCount | 0);
  const start = Math.max(0, Math.min(n - 1, Math.round(index)));
  return { start, length: 1 };
}

/**
 * Loop brace after inserting one pattern at `insertIndex` (0..prevCount).
 * If the insert sits immediately after a pattern already in the brace,
 * grow the brace to include it; otherwise snap onto the new pattern alone.
 */
export function patternLoopAfterInsert(
  prev: PatternLoopRange,
  insertIndex: number,
  prevCount: number,
): PatternLoopRange {
  const n = Math.max(0, prevCount | 0);
  const nextCount = n + 1;
  const insertAt = Math.max(0, Math.min(n, Math.round(insertIndex)));
  if (n === 0) {
    return patternLoopAtIndex(0, nextCount);
  }
  const loop = clampPatternLoop(prev, n);
  const afterIndex = insertAt - 1;
  if (afterIndex >= 0 && patternLoopContainsIndex(loop, afterIndex, n)) {
    return clampPatternLoop({ start: loop.start, length: loop.length + 1 }, nextCount);
  }
  return patternLoopAtIndex(insertAt, nextCount);
}

/** True when `index` lies inside the (clamped) loop range. */
export function patternLoopContainsIndex(
  range: PatternLoopRange,
  index: number,
  patternCount: number,
): boolean {
  const loop = clampPatternLoop(range, patternCount);
  const i = Math.round(index);
  return i >= loop.start && i < loop.start + loop.length;
}

export function patternIdsInLoop(patterns: Pattern[], range: PatternLoopRange): string[] {
  const loop = clampPatternLoop(range, patterns.length);
  return patterns.slice(loop.start, loop.start + loop.length).map(p => p.id);
}

/** Total tracker/play steps covered by the loop range. */
export function patternLoopSteps(patterns: Pattern[], range: PatternLoopRange): number {
  let total = 0;
  for (const id of patternIdsInLoop(patterns, range)) {
    const pattern = patterns.find(p => p.id === id);
    total += patternSteps(pattern);
  }
  return total;
}

export function patternLoopFromClip(clip: ClipIR, range: PatternLoopRange): PatternLoopRange {
  return clampPatternLoop(range, clip.patterns.length);
}

/** Map a global step in a stacked view to its pattern + local step. */
export function locateViewStep(
  patterns: Pattern[],
  viewPatternIds: readonly string[],
  globalStep: number,
): ViewStepRef | null {
  if (viewPatternIds.length === 0) {
    return null;
  }
  let cursor = Math.max(0, Math.round(globalStep));
  for (let viewIndex = 0; viewIndex < viewPatternIds.length; viewIndex++) {
    const patternId = viewPatternIds[viewIndex]!;
    const pattern = patterns.find(p => p.id === patternId);
    const steps = Math.max(1, patternSteps(pattern));
    if (cursor < steps || viewIndex === viewPatternIds.length - 1) {
      return {
        patternId,
        localStep: Math.min(steps - 1, cursor),
        viewIndex,
      };
    }
    cursor -= steps;
  }
  return null;
}

/** Global step index for a pattern-local row inside a stacked view. */
export function locateGlobalStep(
  patterns: Pattern[],
  viewPatternIds: readonly string[],
  patternId: string,
  localStep: number,
): number | null {
  let offset = 0;
  for (const id of viewPatternIds) {
    const pattern = patterns.find(p => p.id === id);
    const steps = Math.max(1, patternSteps(pattern));
    if (id === patternId) {
      return offset + Math.max(0, Math.min(steps - 1, Math.round(localStep)));
    }
    offset += steps;
  }
  return null;
}

export function viewStepCount(
  patterns: Pattern[],
  viewPatternIds: readonly string[],
): number {
  let total = 0;
  for (const id of viewPatternIds) {
    const pattern = patterns.find(p => p.id === id);
    total += Math.max(1, patternSteps(pattern));
  }
  return Math.max(0, total);
}
